import { pokerCardsOnly } from './cards.js';
import { evaluate7 } from './handEval.js';
import { getOpponentMeta, ARCHETYPES } from './aiProfiles.js';
import {
  maxBetRound,
  potThisRound,
  countActivePlayers,
  potOdds,
  classifyPreflop,
  classifyPostflopMade,
  classifyDraw,
  classifyBoardTexture,
  getPositionBucket,
  boardTopRank,
} from './aiBuckets.js';

const BB = 50;

/**
 * @param {Record<string, number>} scores
 * @param {() => number} rng
 * @param {string[]} allowed
 */
function weightedPick(scores, rng, allowed) {
  const keys = allowed.filter((k) => (scores[k] || 0) > 0);
  if (keys.length === 0) return allowed[0];
  const w = keys.map((k) => Math.max(0.01, scores[k]));
  const sum = w.reduce((a, b) => a + b, 0);
  let r = rng() * sum;
  for (let i = 0; i < keys.length; i += 1) {
    r -= w[i];
    if (r <= 0) return keys[i];
  }
  return keys[keys.length - 1];
}

/**
 * @param {'easy'|'medium'|'hard'} diff
 * @param {Record<string, number>} s
 * @param {'preflop'|'post'} phase
 */
function applyDifficulty(diff, s, phase) {
  if (diff === 'easy') {
    s.raise *= 0.75;
    if (phase === 'post') {
      s.raise *= 0.65;
      s.fold *= 1.15;
    }
  } else if (diff === 'hard') {
    s.raise *= 1.15;
    s.fold *= 0.9;
  }
}

/**
 * @param {import('./aiProfiles.js').AiPersonality} pers
 * @param {Record<string, number>} s
 * @param {boolean} isBluff
 */
function applyPersonality(pers, s, isBluff) {
  s.fold *= 0.4 + pers.tightness;
  s.call *= 0.5 + pers.callStationFactor;
  if (isBluff) {
    s.raise *= pers.bluffFrequency * 3.5;
    s.fold *= 1.1 - pers.bluffFrequency * 0.2;
  } else {
    s.raise *= 0.45 + pers.aggression;
  }
}

/**
 * @param {object} state
 * @param {number} seat
 * @param {() => number} rng
 * @returns {{ action: 'fold'|'check'|'call'|'raise', raiseExtra: number }}
 */
export function decideCpuAction(state, seat, rng = Math.random) {
  const players = state.players;
  const p = players[seat];
  const meta = getOpponentMeta(state, seat);
  const pers = meta?.personality || ARCHETYPES.tight_passive;
  const diff = state.aiDifficulty || 'medium';

  const mb = maxBetRound(players);
  const toCall = mb - p.betThisRound;
  const pot = potThisRound(players);
  const hole = p.hole || [];
  const board = state.community;

  /** @type {Record<string, number>} */
  let scores = { fold: 0.15, check: 0.15, call: 0.15, raise: 0.15 };

  if (state.street === 'preflop') {
    const pf = classifyPreflop(hole);
    const pos = getPositionBucket(state.dealerSeat, seat);
    scores = scorePreflop(pf, pos, toCall, pot, pers, diff, p.stack, rng);
  } else {
    const ev = evaluate7(pokerCardsOnly([...hole, ...board]));
    const btop = boardTopRank(board);
    const made = classifyPostflopMade(hole, board, ev, btop);
    const draw = classifyDraw(hole, board);
    const tex = classifyBoardTexture(board);
    const pos = getPositionBucket(state.dealerSeat, seat);
    const active = countActivePlayers(players);
    scores = scorePostflop({
      made,
      draw,
      tex,
      pos,
      toCall,
      pot,
      pers,
      diff,
      street: state.street,
      active,
      stack: p.stack,
      rng,
    });
  }

  const postMade = scores._made;
  const texForSize = scores._tex || 'dry';
  const madeForSize = scores._madeForSize || 'medium';
  const isBluff = state.street !== 'preflop'
    && (scores._bluff || false)
    && ['air', 'weak_showdown'].includes(postMade || '');

  applyPersonality(pers, scores, !!isBluff);
  applyDifficulty(diff, scores, state.street === 'preflop' ? 'preflop' : 'post');

  if (toCall <= 0) {
    scores.call = 0;
  } else {
    scores.check = 0;
  }

  if (toCall >= p.stack && toCall > 0) {
    scores.raise = 0;
    scores.check = 0;
  }

  if (p.stack <= toCall && toCall > 0) {
    scores.raise = 0;
  }

  const canRaise = p.stack > toCall;
  if (!canRaise) scores.raise = 0;

  const allowed = ['fold', 'check', 'call', 'raise'].filter((k) => {
    if (k === 'check' && toCall > 0) return false;
    if (k === 'call' && toCall <= 0) return false;
    if (k === 'raise' && !canRaise) return false;
    if (k === 'fold' && toCall <= 0) return false;
    return (scores[k] || 0) > 0;
  });

  if (allowed.length === 0) {
    if (toCall > 0) {
      if (p.stack <= toCall) return { action: 'call', raiseExtra: 0 };
      return { action: 'fold', raiseExtra: 0 };
    }
    return { action: 'check', raiseExtra: 0 };
  }

  delete scores._bluff;
  delete scores._made;
  delete scores._madeForSize;
  delete scores._tex;

  const action = /** @type {'fold'|'check'|'call'|'raise'} */ (weightedPick(scores, rng, allowed));
  let raiseExtra = 0;
  if (action === 'raise') {
    raiseExtra = chooseRaiseExtra(state, pot, toCall, p.stack, state.street, texForSize, madeForSize, pers, rng);
  }

  return { action, raiseExtra };
}

/**
 * @param {string} pf
 * @param {import('./aiBuckets.js').PositionBucket} pos
 */
function scorePreflop(pf, pos, toCall, pot, pers, diff, stack, rng) {
  /** @type {Record<string, number>} */
  const s = { fold: 0.2, check: 0.15, call: 0.2, raise: 0.2, _madeForSize: 'medium', _tex: 'dry' };

  if (toCall === 0) {
    s.call = 0;
    if (pf === 'premium') {
      s.raise = 8 + 2 * rng();
      s.check = 0.15 * pers.trapFrequency;
    } else if (pf === 'strong') {
      s.raise = 5 + 3 * pers.aggression + 2 * rng();
      s.check = 0.4 * pers.trapFrequency;
    } else if (pf === 'playable') {
      s.raise = 2 + 2 * pers.aggression;
      if (pos === 'early') s.raise *= 0.35 + 0.35 * (1 - pers.tightness);
      if (pos === 'late') s.raise *= 1.35;
      if (pos === 'blinds') s.check = 1.2 + pers.callStationFactor;
    } else if (pf === 'marginal') {
      s.raise = 0.4 + pers.aggression;
      if (pos === 'late') s.raise *= 1.6;
      if (pos === 'early') s.raise *= 0.25;
      s.check = 0.8 + 0.5 * pers.callStationFactor;
    } else {
      s.raise = 0.05 + 0.15 * (1 - pers.tightness) * (pos === 'late' ? 1.5 : 0.3);
      s.check = 0.5;
    }
  } else {
    s.check = 0;
    const po = potOdds(toCall, pot);
    if (pf === 'premium') {
      s.call = 6;
      s.raise = 5 + 4 * pers.aggression;
      s.fold = 0;
    } else if (pf === 'strong') {
      s.call = 4 + 2 * pers.riskTolerance;
      s.raise = 1.5 * pers.aggression;
      s.fold = 0.15;
    } else if (pf === 'playable') {
      s.call = 2 + 2.5 * pers.callStationFactor + (pos === 'late' ? 0.8 : 0);
      s.raise = 0.35 * pers.aggression * (diff === 'hard' ? 1.2 : 0.4);
      s.fold = 0.8 * pers.tightness;
      if (po > 0.35) s.fold += 0.3;
    } else if (pf === 'marginal') {
      s.call = 0.6 * pers.callStationFactor + (pos === 'blinds' ? 0.5 : 0);
      s.fold = 1.2 * pers.tightness;
      s.raise = 0.08 * pers.aggression * (diff === 'hard' ? 1 : 0.2);
      if (toCall > 3 * BB) s.fold += 0.5;
    } else {
      s.fold = 2 * pers.tightness;
      s.call = 0.15 * pers.callStationFactor;
      s.raise = 0;
    }

    if (toCall >= stack * 0.95 && stack > 0) {
      s.raise = 0;
      if (pf === 'premium') s.call = 12;
      else if (pf === 'strong') s.call = 4 * pers.riskTolerance * (diff === 'easy' ? 0.7 : 1);
      else if (pf === 'playable' && (diff === 'hard' || stack < 20 * BB)) s.call = 1.5 * pers.riskTolerance;
      else if (pf === 'marginal' && diff === 'hard' && pers.riskTolerance > 0.6) s.call = 0.8;
      else {
        s.call = 0.3;
        s.fold = 2;
      }
    }
  }

  return s;
}

/**
 * @param {object} ctx
 */
function scorePostflop(ctx) {
  const {
    made, draw, tex, pos, toCall, pot, pers, diff, street, active, stack, rng,
  } = ctx;

  /** @type {Record<string, number>} */
  const s = {
    fold: 0.2,
    check: 0.2,
    call: 0.2,
    raise: 0.2,
    _bluff: false,
    _made: made,
    _madeForSize: made,
    _tex: tex,
  };

  const po = potOdds(toCall, pot);
  const multiway = active >= 3;

  if (toCall === 0) {
    s.call = 0;
    if (made === 'monster' || made === 'very_strong') {
      s.raise = 7 + 2 * rng();
      s.check = 0.5 + 2 * pers.trapFrequency * (tex === 'dry' ? 1 : 0.3);
    } else if (made === 'strong') {
      s.raise = 4 + 2 * pers.aggression;
      s.check = 0.8 + 0.5 * pers.trapFrequency;
    } else if (made === 'medium') {
      s.raise = tex === 'dry' ? 1.5 + pers.aggression : 0.6;
      s.check = 2;
    } else if (made === 'weak_showdown') {
      s.check = 2.2;
      s.raise = tex === 'dry' && !multiway ? 0.4 * pers.aggression : 0.1;
    } else {
      s.check = 1.5;
      s._bluff = true;
      s.raise = !multiway && pos === 'late' && tex === 'dry'
        ? 0.35 * pers.bluffFrequency * (diff === 'easy' ? 0.35 : 1)
        : 0.12 * pers.bluffFrequency;
    }

    if (draw === 'strong' && made === 'air') {
      s.raise += 1.2 * pers.aggression;
      s._bluff = true;
    } else if (draw === 'medium' && street !== 'river') {
      s.raise += 0.7 * pers.aggression;
      s._bluff = true;
    }

    if (multiway) {
      s.raise *= 0.55;
      s._bluff = false;
    }

    if (street === 'river') {
      s.raise *= made === 'air' ? 0.25 : 0.85;
      if (made === 'air') s._bluff = diff !== 'easy' && rng() < 0.15 * pers.bluffFrequency;
    }
  } else {
    s.check = 0;
    if (made === 'monster' || made === 'very_strong') {
      s.call = 3;
      s.raise = 4 + 2 * pers.aggression + 2 * rng();
      s.fold = 0;
    } else if (made === 'strong') {
      s.call = 3.5;
      s.raise = 1.5 * pers.aggression;
      s.fold = 0.1;
    } else if (made === 'medium') {
      s.call = 2 + 2 * pers.callStationFactor + (pos === 'late' ? 0.5 : 0);
      s.fold = tex === 'wet' && toCall > pot * 0.45 ? 1.2 : 0.4;
      s.raise = 0.25 * pers.aggression;
      if (po > 0.42) s.fold += 0.35;
    } else if (made === 'weak_showdown') {
      s.call = 1 * pers.callStationFactor + (toCall < pot * 0.28 ? 0.8 : 0);
      s.fold = 1 + pers.tightness;
      if (diff === 'easy' && toCall > pot * 0.5) s.fold += 0.8;
    } else {
      s.fold = 1.5 + pers.tightness;
      s.call = draw === 'strong' && po < 0.32 ? 1.2 : draw === 'medium' && po < 0.28 ? 0.9 : 0.2 * pers.callStationFactor;
      s.raise = 0;
      if (draw === 'strong' && street !== 'river') {
        s.raise = 0.8 * pers.aggression * (diff === 'easy' ? 0.5 : 1);
        s._bluff = true;
      }
      if (draw === 'medium' && po < 0.25 && street === 'flop') {
        s.call += 0.8;
      }
    }

    if (street === 'turn') {
      s.raise *= made === 'air' ? 0.45 : 0.85;
    }
    if (street === 'river') {
      s.raise *= 0.35;
      if (made === 'medium' || made === 'weak_showdown') {
        if (toCall > pot * (diff === 'easy' ? 0.55 : 0.75)) s.fold += 0.9;
      }
      if (made === 'air') {
        s.call *= 0.3;
        s.fold += 0.5;
      }
    }

    if (toCall >= stack * 0.98 && stack > 0) {
      s.raise = 0;
      if (['monster', 'very_strong'].includes(made)) s.call = 10;
      else if (made === 'strong') s.call = 3 * pers.riskTolerance;
      else if (made === 'medium' && draw === 'strong') s.call = 1.5 * pers.riskTolerance;
      else {
        s.call = 0.4 * pers.riskTolerance;
        s.fold = 2;
      }
    }
  }

  return s;
}

function chooseRaiseExtra(state, pot, toCall, stack, street, texture, made, pers, rng) {
  const minR = state.lastRaiseIncrement;
  const avail = Math.max(0, stack - toCall);
  if (avail <= 0) return 0;

  if (state.street === 'preflop') {
    const openMult = 2 + rng() * (1 + pers.aggression);
    const target = openMult * BB;
    return Math.min(avail, Math.max(minR, target - toCall));
  }

  if (toCall > 0) {
    const mult = 2 + rng() + pers.aggression * 0.8;
    return Math.min(avail, Math.max(minR, mult * toCall));
  }

  let frac = 0.5;
  if (street === 'flop') {
    frac = texture === 'dry' ? 0.33 : texture === 'wet' ? 0.62 : 0.45;
  } else if (street === 'turn') {
    frac = 0.55 + 0.12 * rng();
    if (texture === 'wet') frac += 0.08;
  } else {
    frac = 0.52 + 0.22 * rng();
  }

  if (made === 'monster' || made === 'very_strong') frac += 0.12;
  if (made === 'air') frac *= 0.65;

  frac *= 0.85 + 0.25 * pers.aggression;
  return Math.min(avail, Math.max(minR, Math.round(frac * Math.max(pot, BB))));
}
